import React, { useState, useRef, useEffect } from "react";
import { useDraft, useLocalWizardDraft } from "@/lib/drafts";
import { goToCheckout } from "@/lib/checkout";
import FoundThanksStep from "@/components/FoundThanksStep";
import { FormHeader, StepProgress } from "@/components/FormShell";
import SubmitOverlay from "@/components/SubmitOverlay";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { WizardViews, WizardAuthStep } from "@/components/DeclareWizard";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { RecaptchaNotice } from "@/components/RecaptchaNotice";
import { fileToCompressedDataUrl } from "@/lib/image";
import PhotoEditor from "@/components/PhotoEditor";
import CategorySelect from "@/components/CategorySelect";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/lib/images";
import { countryFlag, countryName } from "@/lib/countries";
import { labelFor } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { CountrySelect } from "@/components/CountrySelect";
import { CheckCircle2, Upload, Eye, MapPin, Facebook, MessageCircle, Link2, Download, PackageCheck, Trophy, ArrowLeft, ArrowRight, HandHeart, RefreshCw } from "lucide-react";
import { XIcon } from "@/components/XIcon";
import { InstagramIcon, TikTokIcon } from "@/components/BrandIcons";
import { ItemDetailFields, accountIdentity, MissingPhoneField, savePhoneIfMissing, phoneMissing, EXTRA_DEFAULTS, extraPayload, usePrefillFromProfile } from "@/components/DeclarationFields";
import { FindoSpinner } from "@/components/FindoSpinner";
import { securePhoto } from "@/lib/photoSecurity";

export default function DeclareFound() {
  const { t } = useLang();
  const { user, refreshUser } = useB2C();
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  const [created, setCreated] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [step, setStep] = useState(() => { const q = new URLSearchParams(window.location.search); return q.get("payment_cancelled") === "1" && q.get("resume_draft") === "1" ? 2 : 1; });
  const formTopRef = useRef(null);
  const goStep = (n) => { setStep(n); setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30); };
  const wizardTopRef = useRef(null);
  // Just signed in at the wizard's account step → bring the publication step into view.
  useEffect(() => { if (user && step === 2) setTimeout(() => wizardTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 420); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onRelaunch = () => goStep(2);
    window.addEventListener("findloop:relaunch-payment", onRelaunch);
    return () => window.removeEventListener("findloop:relaunch-payment", onRelaunch);
  }, []);
  const [editorSrc, setEditorSrc] = useState(null);
  const [editorTarget, setEditorTarget] = useState("image_base64");
  const [form, setForm] = useState({
    category: "", description: "", lost_date: "", lost_place: "",
    country: "FR", image_base64: null, image_base64_2: null, share_consent: new URLSearchParams(window.location.search).get("payment_cancelled") === "1", contribution: 20,
    verification_questions: ["", "", ""],
    ...EXTRA_DEFAULTS,
  });
  usePrefillFromProfile(user, setForm);
  const draft = useDraft("found_ad", form, setForm, { enabled: !!user, label: form.description });
  const localDraft = useLocalWizardDraft("found_ad", { form, setForm, step, setStep });
  // Back from a cancelled payment: consent was already given before Stripe, keep it ticked after the draft restore.
  const consentKept = useRef(new URLSearchParams(window.location.search).get("payment_cancelled") === "1");
  useEffect(() => {
    if (consentKept.current && form.description && !form.share_consent) {
      consentKept.current = false;
      setForm((f) => ({ ...f, share_consent: true }));
    }
  }, [form.description, form.share_consent]);

  async function onFile(e, slot = "image_base64") {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const compressed = await fileToCompressedDataUrl(f, { maxDim: 2000, quality: 0.9 });
      setEditorTarget(slot);
      setEditorSrc(compressed);
    } catch (err) {
      toast.error(t("pub.cantProcessImage"));
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.category) { toast.error(t("fa.categoryRequired")); return; }
    if (!form.image_base64) { toast.error(t("fr.photoError")); return; }
    if (step === 1) { goStep(2); return; }
    if (!form.share_consent) { toast.error(t("anf.consent")); return; }
    if (phoneMissing(user, form, "found")) { toast.error(t("acc.phoneRequired")); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    try {
      await savePhoneIfMissing(user, accountIdentity(user, form).owner_phone, refreshUser);
      const res = await api("/b2c/found-ads", {
        method: "POST", b2c: true,
        body: {
          category: form.category, description: form.description, image_base64: form.image_base64,
          image_base64_2: form.image_base64_2,
          lost_date: form.lost_date, lost_place: form.lost_place, country: form.country,
          share_consent: form.share_consent, ...extraPayload({ ...form, ...accountIdentity(user, form) }),
          verification_questions: (form.verification_questions || []).map((q) => q.trim()).filter(Boolean),
          contribution_eur: form.contribution, origin_url: window.location.origin, return_path: draft.resumePath,
          captcha_token: await getRecaptchaToken("report_found"),
        },
      });
      if (res?.url) { await draft.saveNow(); localDraft.clear(); toast.message(t("fa.redirectStripe")); goToCheckout(res.url, { returnTo: draft.resumePath }); return; }
      draft.clear();
      localDraft.clear();
      setCreated(res);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); submittingRef.current = false; }
  }

  // Wizard: 1) declaration (no account needed) → 2) login / sign-up if needed → 3) publication.
  const wizardLabels = [t("form.stepDetails"), t("form.stepAccount"), t("form.stepThanks")];
  const view = created ? "done" : step === 1 ? "details" : !user ? "auth" : "publish";

  return (
    <div className="bg-background">
      <div className="relative h-64 sm:h-72 overflow-hidden photo-scrim">
        <motion.img
          initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          src={IMAGES.heroFound} alt=""
          className="w-full h-full object-cover object-[center_30%] animate-ken-burns" data-testid="declare-found-banner" />
        <div className="absolute inset-0 bg-[#191A54]/45" aria-hidden="true" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <motion.span
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide" data-testid="found-badge">{t("anf.badge")}</motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }}
            className="mt-4 font-display text-4xl md:text-5xl font-semibold" data-testid="found-form-title">{t("anf.postTitle")}</motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }}
            className="mt-2 text-white/85 max-w-lg">{t("anf.postSubtitle")}</motion.p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div ref={wizardTopRef} className="scroll-mt-24" />
        <WizardViews view={view}>
        {created ? (
          <div className="bg-white rounded-2xl border border-border card-shadow p-8 text-center" data-testid="found-ad-created">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-teal-100 text-teal-700"><CheckCircle2 size={34} /></div>
            <h2 className="mt-5 font-display text-2xl font-semibold text-findo-navy">{t("anf.published")}</h2>
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1.5 text-sm font-semibold text-amber-700" data-testid="found-points-earned">
              <Trophy size={15} /> {t("anf.pointsEarned")}
            </p>
            <p className="mt-1.5 text-xs text-slate-400">{t("anf.pointsPending")}</p>
            <ShareableAd adId={created.id} t={t} />
            <Link to="/objets-trouves"><Button variant="ghost" className="mt-5 rounded-full font-semibold" data-testid="found-browse-btn">{t("anf.title")}</Button></Link>
          </div>
        ) : view === "auth" ? (
          <WizardAuthStep t={t} title={t("anf.loginRequired")} subtitle={t("anf.loginDesc")} labels={wizardLabels} onBack={() => goStep(1)} />
        ) : (
          <form ref={formTopRef} onSubmit={submit} className="findo-form form-card bg-white rounded-3xl border border-border overflow-hidden scroll-mt-24" data-testid="post-found-form">
            <SubmitOverlay show={busy} payment={Number(form.contribution) > 0} />
            <FormHeader icon={HandHeart} title={t("anf.postTitle")} subtitle={t("anf.postSubtitle")} testid="found-form-header">
              <StepProgress step={step === 1 ? 1 : 3} total={3} labels={wizardLabels} t={t} />
            </FormHeader>
            <div className="findo-form-body p-6 sm:p-8 space-y-6">
            {step === 1 ? (<>
            <div><Label>{t("dec.category")} <span className="text-findo-red">*</span></Label>
              <div className="mt-1.5">
                <CategorySelect value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="found-category" />
              </div>
            </div>
            <div><Label>{t("dec.description")} <span className="text-findo-red">*</span></Label><Textarea required minLength={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={(() => { const l = t("dec.descPhLong"); return l === "dec.descPhLong" ? t("hc.describeTheItemBrandColor") : l; })()} className="mt-1.5 rounded-xl" data-testid="found-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("anf.foundDate")}</Label><Input type="date" required value={form.lost_date} onChange={(e) => setForm({ ...form, lost_date: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="found-date" /></div>
              <div><Label>{t("anf.foundAt")} <span className="text-findo-red">*</span></Label><div className="mt-1.5"><CityAutocomplete value={form.lost_place} onChange={(v) => setForm({ ...form, lost_place: v })} country={form.country} placeholder={t("hc.parisGareDeLyon")} className="h-11 rounded-xl" testid="found-place" required /></div></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("mc.country")} <span className="text-findo-red">*</span></Label>
                <div className="mt-1.5">
                  <CountrySelect value={form.country} onChange={(v) => setForm({ ...form, country: v, _countryTouched: true })} testid="found-country" />
                </div>
              </div>
            </div>
            <ItemDetailFields form={form} setForm={setForm} prefix="found" found />
            <div>
              <Label>{t("anf.photo")} <span className="text-findo-red">*</span></Label>
              <label className="mt-1.5 flex items-center gap-2 h-11 px-3 rounded-xl border border-dashed border-border cursor-pointer text-sm text-slate-500 hover:border-findo-navy" data-testid="found-photo-label">
                <Upload size={16} /> {form.image_base64 ? t("pub.photoAdded") : t("anf.photo")}
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="found-photo" />
              </label>
              {form.image_base64 && (
                <motion.img
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
                  src={form.image_base64} alt={t("pub.previewAlt")}
                  className="mt-2 w-full h-48 object-contain bg-slate-50 rounded-xl border border-border" data-testid="found-photo-preview" />
              )}
              {form.image_base64 && (
                <label className="mt-2 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-border text-xs font-semibold text-findo-navy cursor-pointer hover:bg-slate-50 transition-colors" data-testid="found-photo-change">
                  <RefreshCw size={13} /> {t("pub.changePhoto")}
                  <input type="file" accept="image/*" onChange={(e) => onFile(e, "image_base64")} className="hidden" data-testid="found-photo-change-input" />
                </label>
              )}
              {form.image_base64 && (
                <div className="mt-3" data-testid="found-photo2-block">
                  <label className="flex items-center gap-2 h-11 px-3 rounded-xl border border-dashed border-border cursor-pointer text-sm text-slate-500 hover:border-findo-navy" data-testid="found-photo2-label">
                    <Upload size={16} /> {form.image_base64_2 ? t("pub.photo2Added") : t("pub.addSecondPhoto")}
                    <input type="file" accept="image/*" onChange={(e) => onFile(e, "image_base64_2")} className="hidden" data-testid="found-photo2" />
                  </label>
                  {form.image_base64_2 && (
                    <div className="relative mt-2">
                      <img src={form.image_base64_2} alt={t("pub.previewAlt")}
                        className="w-full h-40 object-cover rounded-xl border border-border" data-testid="found-photo2-preview" />
                      <button type="button" onClick={() => setForm((s) => ({ ...s, image_base64_2: null }))}
                        className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80 text-lg leading-none" data-testid="found-photo2-remove">×</button>
                    </div>
                  )}
                  {form.image_base64_2 && (
                <label className="mt-2 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-border text-xs font-semibold text-findo-navy cursor-pointer hover:bg-slate-50 transition-colors" data-testid="found-photo2-change">
                  <RefreshCw size={13} /> {t("pub.changePhoto")}
                  <input type="file" accept="image/*" onChange={(e) => onFile(e, "image_base64_2")} className="hidden" data-testid="found-photo2-change-input" />
                </label>
                  )}
                </div>
              )}
            </div>

            {user && (
              <div className="rounded-xl bg-slate-50 border border-border px-3 py-2.5 text-xs text-slate-500" data-testid="found-published-as">
                {t("anf.publishedAs")} <span className="font-semibold text-findo-navy">{user.name}</span> · {user.email}
              </div>
            )}


            <Button type="submit" disabled={!form.image_base64} className="w-full h-12 rounded-xl font-semibold bg-findo-red hover:bg-findo-red/90" data-testid="found-next-step">
              {t("form.next")} <ArrowRight size={17} className="ml-1.5" />
            </Button>
            </>) : (<>
            <FoundThanksStep value={form.contribution} onChange={(v) => setForm((f) => ({ ...f, contribution: v }))} />
            <MissingPhoneField user={user} form={form} setForm={setForm} prefix="found" />
            <label className="flex items-start gap-2.5 rounded-xl bg-slate-50 border border-border p-3 cursor-pointer" data-testid="found-consent-label">
              <input type="checkbox" checked={form.share_consent} onChange={(e) => setForm({ ...form, share_consent: e.target.checked })} className="mt-0.5 h-4 w-4 accent-findo-navy" data-testid="found-consent" />
              <span className="text-xs text-slate-600 leading-relaxed">{t("anf.consent")}</span>
            </label>

            <Button type="button" variant="outline" onClick={() => (form.category ? setPreviewOpen(true) : toast.error(t("fa.categoryRequired")))} className="w-full h-11 rounded-xl font-semibold border-findo-navy text-findo-navy hover:bg-findo-navy/5" data-testid="found-preview-btn">
              <Eye size={16} className="mr-1.5" /> {t("pub.adPreviewBtn")}
            </Button>
            <Button type="submit" disabled={busy || !form.share_consent} className="w-full h-12 rounded-xl font-semibold bg-teal-600 hover:bg-teal-600/90" data-testid="found-submit">
              <PackageCheck size={18} className="mr-1.5" /> {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("an.publishing")}</span> : t("anf.publishFound")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => goStep(1)} className="w-full h-11 rounded-xl font-semibold text-findo-navy" data-testid="found-prev-step">
              <ArrowLeft size={16} className="mr-1.5" /> {t("form.back")}
            </Button>
            <AdPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} form={form} onPublish={() => { setPreviewOpen(false); if (form.share_consent) submit({ preventDefault() {} }); else toast.error(t("anf.consent")); }} busy={busy} t={t} />
            </>)}
            <RecaptchaNotice className="mt-3" />
            </div>
          </form>
        )}
        </WizardViews>
      </div>
      <PhotoEditor src={editorSrc} open={!!editorSrc}
        onClose={() => setEditorSrc(null)}
        onSave={async (b64) => { const safe = await securePhoto(b64, "ad", t); if (safe) setForm((s) => ({ ...s, [editorTarget]: safe })); }} />
    </div>
  );
}

function ShareableAd({ adId, t }) {
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/s/${adId}`;
  const imgUrl = `${API}/public-ads/${adId}/share-image.png`;
  const enc = encodeURIComponent;
  const text = t("hc.shareFoundText");
  const links = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(text)}`,
    whatsapp: `https://wa.me/?text=${enc(text + " " + shareUrl)}`,
  };
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  async function copy() {
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("pub.linkCopied")); }
    catch { toast.error(t("pub.copyFailed")); }
  }
  async function shareSocial(label) {
    if (navigator.share) {
      try { await navigator.share({ title: "findloop", text, url: shareUrl }); return; }
      catch (e) { if (e && e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(shareUrl); toast.success(t("hc.linkCopiedShareOn").replace("{label}", label)); }
    catch { toast.error(t("pub.copyFailed")); }
  }
  const btn = "h-10 w-10 grid place-items-center rounded-full border border-border text-slate-500 hover:text-findo-navy hover:border-findo-navy transition-colors";
  return (
    <div className="mt-5 text-left" data-testid="found-shareable-ad">
      <p className="text-sm font-semibold text-findo-navy text-center">{t("pub.shareYourAd")}</p>
      <p className="text-xs text-slate-500 text-center mt-0.5">{t("pub.spreadOwner")}</p>
      <img src={imgUrl} alt={t("pub.sharePreview")}
        className="mt-3 w-full rounded-xl border border-border" data-testid="found-shareable-image" />
      <div className="mt-3 flex items-center justify-center gap-2">
        <button type="button" onClick={() => open(links.facebook)} className={btn} style={{ color: "#1877F2" }} title="Facebook" data-testid="found-share-facebook"><Facebook size={17} /></button>
        <button type="button" onClick={() => open(links.twitter)} className={btn} style={{ color: "#000000" }} title="X / Twitter" data-testid="found-share-twitter"><XIcon size={17} /></button>
        <button type="button" onClick={() => open(links.whatsapp)} className={btn} style={{ color: "#25D366" }} title="WhatsApp" data-testid="found-share-whatsapp"><MessageCircle size={17} /></button>
        <button type="button" onClick={() => shareSocial("Instagram")} className={btn} title="Instagram" data-testid="found-share-instagram"><InstagramIcon size={17} /></button>
        <button type="button" onClick={() => shareSocial("TikTok")} className={btn} title="TikTok" data-testid="found-share-tiktok"><TikTokIcon size={17} /></button>
        <button type="button" onClick={copy} className={btn} title={t("an.copyLink")} data-testid="found-share-copy"><Link2 size={17} /></button>
        <a href={imgUrl} download={`annonce-${adId}.png`} className={btn} title={t("pub.downloadImage")} data-testid="found-share-download"><Download size={17} /></a>
      </div>
    </div>
  );
}

function AdPreviewDialog({ open, onClose, form, onPublish, busy, t }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="found-preview-dialog">
        <DialogHeader>
          <DialogTitle icon={Eye}>{t("pub.yourAdPreview")}</DialogTitle>
          <DialogDescription>{t("pub.adAppearPublic")}</DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-border overflow-hidden">
          {form.image_base64 ? (
            <img src={form.image_base64} alt={t("pub.previewAlt")} className="w-full h-44 object-cover" data-testid="found-preview-photo" />
          ) : (
            <div className="w-full h-44 bg-slate-100 grid place-items-center text-slate-400 text-sm">{t("pub.noPhoto")}</div>
          )}
          <div className="p-4 space-y-2">
            <span className="inline-flex items-center rounded-full bg-teal-600/10 text-teal-700 px-2.5 py-1 text-xs font-bold" data-testid="found-preview-category">{labelFor(form.category)}</span>
            <p className="text-sm text-slate-700" data-testid="found-preview-description">{form.description || "—"}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 pt-1">
              <span className="inline-flex items-center gap-1" data-testid="found-preview-place"><MapPin size={13} /> {form.lost_place || "—"} · {countryFlag(form.country)} {countryName(form.country)}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1 rounded-full font-semibold" data-testid="found-preview-edit-btn">{t("pub.edit")}</Button>
          <Button onClick={onPublish} disabled={busy} className="flex-1 rounded-full font-semibold bg-teal-600 hover:bg-teal-600/90" data-testid="found-preview-publish-btn">
            {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("pub.publishingWord")}</span> : t("pub.publish")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

